import type { OcrQaStage } from '../types';

/**
 * Which screen is rendering the results panel.
 *
 * Not optional, deliberately. The panel used to infer read-only from nothing
 * but "am I previewing archived history", and the QA detail page rendered it
 * with no editing prop at all — which is how re-run OCR, re-run page,
 * accept-all, submit-to-QA, draw-highlight and the colour picker all ended up
 * on a QA reviewer's screen. A required discriminator makes that omission
 * impossible to repeat.
 */
export type OcrPanelSurface = 'ocr' | 'qa' | 'supervisor';

export interface PanelCapabilities {
  /** Whether field values may be accepted or corrected from this surface. */
  fieldsEditable: boolean;
  /**
   * Whether the OCR-operator tools render: re-run the run, re-run one page,
   * accept a whole page, submit to QA, draw a highlight, recolour a field.
   */
  operatorToolsVisible: boolean;
}

/**
 * Whether the operator still owns a container's data — the frontend mirror of
 * `OcrResult::isOperatorEditable()`. True before submission and again after a
 * tier bounces the container back, which is the entire point of a reject.
 *
 * `qa_completed` is treated as locked here even though the backend currently
 * still allows it: there is no reason for the UI to offer an edit that nobody
 * will review.
 */
export function isOperatorOwned(stage: OcrQaStage | null): boolean {
  return stage == null || stage === 'qa_rejected';
}

export function resolvePanelCapabilities(input: {
  surface: OcrPanelSurface;
  containerQaStage: OcrQaStage | null;
  isPreviewMode: boolean;
  /**
   * QA surface only: whether the container is actionable at THIS tier. The
   * panel cannot work this out — it does not know which tier is rendering it —
   * so the QA screen, which already computes it for its verdict buttons, says
   * so explicitly.
   */
  qaTierActive?: boolean;
}): PanelCapabilities {
  const { surface, containerQaStage, isPreviewMode, qaTierActive } = input;

  // Archived history is frozen on every surface.
  if (isPreviewMode) {
    return { fieldsEditable: false, operatorToolsVisible: false };
  }

  // The supervisor reviews DISCOVERED FIELDS, not values. They decide whether
  // a field belongs in the template at all, which is a template decision made
  // through `/ocr-discovered-fields/*` — they never correct a value and never
  // touch an operator tool, so the panel is pure context here.
  if (surface === 'supervisor') {
    return { fieldsEditable: false, operatorToolsVisible: false };
  }

  if (surface === 'qa') {
    // A reviewer edits through the tier-scoped endpoint, never with the
    // operator's tools — and only while the container is actually theirs.
    return {
      fieldsEditable: qaTierActive === true,
      operatorToolsVisible: false
    };
  }

  const owned = isOperatorOwned(containerQaStage);

  return { fieldsEditable: owned, operatorToolsVisible: owned };
}
